import { money, type Money } from "@stb/core/money"

/** Maximum work-related km per car per income year under the cents per kilometre method. */
export const CENTS_PER_KM_CAP_KM = 5000

export interface CentsPerKmRate {
  /** ATO-style income year label, e.g. `2026-27`. */
  financialYear: string
  /** Rate in whole cents per km. */
  cents: number
  /** ATO page the rate was read from. */
  sourceUrl: string
  /** Date (`YYYY-MM-DD`) the page was opened and the rate checked. */
  checkedOn: string
}

// Individuals page, "Last updated 12 August 2026". Rates for 2022-23 to 2026-27 as listed there.
// Re-open the page and bump `checkedOn` whenever a rate is added or confirmed.
const SOURCE_URL =
  "https://www.ato.gov.au/individuals-and-families/income-deductions-offsets-and-records/deductions-you-can-claim/work-related-deductions/cars-transport-and-travel/motor-vehicle-and-car-expenses/expenses-for-a-car-you-own-or-lease/cents-per-kilometre-method"
const CHECKED_ON = "2026-10-27"

const row = (financialYear: string, cents: number): CentsPerKmRate => ({
  financialYear,
  cents,
  sourceUrl: SOURCE_URL,
  checkedOn: CHECKED_ON,
})

export const CENTS_PER_KM_RATES: readonly CentsPerKmRate[] = Object.freeze([
  row("2022-23", 78),
  row("2023-24", 85),
  row("2024-25", 88),
  row("2025-26", 88),
  row("2026-27", 91),
])

/** Rate for an income year; throws for a year not in the table (never guesses). */
export function centsPerKmRate(financialYear: string): CentsPerKmRate {
  const found = CENTS_PER_KM_RATES.find((r) => r.financialYear === financialYear)
  if (!found) throw new RangeError(`No ATO cents per km rate for income year "${financialYear}"`)
  return found
}

export interface ResolvedCentsPerKmRate {
  /** The table row to use: the income year itself, or the latest published row while the new year's is pending. */
  rate: CentsPerKmRate
  /** True when `rate` is the previous year's row because the ATO rate of the asked-for year is not in the table yet. */
  newYearRatePending: boolean
}

/** `2026-27` -> `2027-28`; null for a label that is not `YYYY-YY`. */
function nextYearLabel(financialYear: string): string | null {
  const m = /^(\d{4})-(\d{2})$/.exec(financialYear)
  if (!m) return null
  const start = Number(m[1]) + 1
  return `${start}-${String((start + 1) % 100).padStart(2, "0")}`
}

/**
 * Rate-year rule for screens that show a rate for "this" income year (logbook comparison, T1234). The year of
 * today's date turns over on 1 July, but the ATO publishes the new cents per km rate some weeks later (the
 * 2026-27 rate: page "Last updated 12 August 2026", see `SOURCE_URL`). For that gap the screen must not
 * disappear: the year right after the latest table row keeps the latest row and is flagged `newYearRatePending`,
 * so the UI can say the new rate is not published yet. Any other year outside the table (earlier, or two or more
 * years ahead, which means the table is stale) gives null: a rate is never guessed.
 */
export function resolveCentsPerKmRate(
  financialYear: string,
  rates: readonly CentsPerKmRate[] = CENTS_PER_KM_RATES,
): ResolvedCentsPerKmRate | null {
  const exact = rates.find((r) => r.financialYear === financialYear)
  if (exact) return { rate: exact, newYearRatePending: false }
  const latest = rates.reduce<CentsPerKmRate | null>(
    (a, r) => (a === null || r.financialYear > a.financialYear ? r : a),
    null,
  )
  if (latest && nextYearLabel(latest.financialYear) === financialYear) {
    return { rate: latest, newYearRatePending: true }
  }
  return null
}

export type CentsPerKmResult =
  | { ok: true; deduction: Money; rate: CentsPerKmRate }
  | { ok: false; reason: "use-logbook-method" }

/** Deduction for whole work-related km in one car; over the cap the caller must use the logbook method. */
export function calculateCentsPerKm(financialYear: string, km: number): CentsPerKmResult {
  if (!Number.isInteger(km) || km < 0) {
    throw new RangeError(`km must be a non-negative whole number, got ${km}`)
  }
  const rate = centsPerKmRate(financialYear)
  if (km > CENTS_PER_KM_CAP_KM) return { ok: false, reason: "use-logbook-method" }
  return { ok: true, deduction: money(km * rate.cents, "AUD"), rate }
}
